import { isEmpty } from 'lodash';
import { formatDuration, getRoundPrice } from '../../../helpers/results';
import {
  BUS,
  CARPOOLING,
  SHORT_FAQ_DAILY_TRIPS,
  SHORT_FAQ_DAILY_TRIPS_HEADERS,
  SHORT_FAQ_DEPARTURE,
  SHORT_FAQ_DEPQRTURE_HEADERS,
  SHORT_FAQ_DURATION,
  SHORT_FAQ_DURATION_HEADERS,
  SHORT_FAQ_PRICE,
  SHORT_FAQ_PRICE_HEADERS,
  TRAIN,
} from '../constants';
import { SearchPageType } from '../types';
import messages from '../View/messages';
import { TextGen } from './TextGen';

/**
 * @description FaqTextGen class that formats the short faqs for trips with historyData (price, duration, departures & daily trips).
 */
class FaqTextGen extends TextGen {
  constructor(textGen) {
    super(textGen);
    this.intl = textGen.intl;
    this.chance = textGen.chance;
    this.seed = textGen.seed;
  }

  /**
   * @description Main function formatting all faqs for train and bus means.
   * @param {object} state - State data
   * @returns {{question: string, id: string, answer: string}[]} Array of faqs with a question and a answer body each
   */
  formatShortFaqs(state) {
    if (
      isEmpty(state.tripData) ||
      !state.tripData.transportInformation.historyData
    ) {
      return [];
    }

    const faqs = [];

    if (
      [SearchPageType.BUS, SearchPageType.GENERIC].includes(
        state.currentPageType,
      )
    ) {
      faqs.push(...this.getMeanFaqs(BUS, state, this.seed));
    }

    if (
      [SearchPageType.TRAIN, SearchPageType.GENERIC].includes(
        state.currentPageType,
      )
    ) {
      faqs.push(...this.getMeanFaqs(TRAIN, state, this.seed + 1));
    }

    if (
      [SearchPageType.CARPOOLING, SearchPageType.GENERIC].includes(
        state.currentPageType,
      )
    ) {
      faqs.push(...this.getMeanFaqs(CARPOOLING, state, this.seed + 2));
    }

    return faqs;
  }

  /**
   * @description Computes the faqs for a transport mean
   * @param {string} mean - Transport mean
   * @param {object} state - State data
   * @param {number} seed - Seed used for this transport mean (to induce variations)
   * @returns {{question: string, id: string, answer: string}[]}
   */
  getMeanFaqs(mean, state, seed) {
    if (isEmpty(state.tripData.transportInformation[mean])) {
      return [];
    }

    const { bestItinerary, nbPerDay } =
      state.tripData.transportInformation[mean];
    const formattedTrips = this.getFormattedTrips(
      state.originCity.name,
      state.destinationCity.name,
      seed,
    );
    const translatedMean = this.intl.formatMessage(messages[mean]);

    const priceFaq = {
      id: `priceFaq-${mean}`,
      question: this.intl.formatMessage(
        messages[
          SHORT_FAQ_PRICE_HEADERS[seed % SHORT_FAQ_PRICE_HEADERS.length]
        ],
        {
          origin: state.originCity.name,
          destination: state.destinationCity.name,
          transportMean: translatedMean,
        },
      ),
      answer: this.intl.formatMessage(
        messages[SHORT_FAQ_PRICE[seed % SHORT_FAQ_PRICE.length]],
        {
          transportMean: translatedMean,
          formattedTrip1: formattedTrips[0],
          formattedTrip2: formattedTrips[1],
          formattedTrip3: formattedTrips[2],
          minPrice: getRoundPrice(bestItinerary.price.min),
          maxPrice: getRoundPrice(bestItinerary.price.max),
          meanPrice: getRoundPrice(bestItinerary.price.mean),
        },
      ),
    };

    const durationFaq = {
      id: `durationFaq-${mean}`,
      question: this.intl.formatMessage(
        messages[
          SHORT_FAQ_DURATION_HEADERS[seed % SHORT_FAQ_DURATION_HEADERS.length]
        ],
        {
          origin: state.originCity.name,
          destination: state.destinationCity.name,
          transportMean: translatedMean,
        },
      ),
      answer: this.intl.formatMessage(
        messages[SHORT_FAQ_DURATION[seed % SHORT_FAQ_DURATION.length]],
        {
          origin: state.originCity.name,
          destination: state.destinationCity.name,
          transportMean: translatedMean,
          formattedTrip1: formattedTrips[0],
          formattedTrip2: formattedTrips[1],
          minDuration: formatDuration(bestItinerary.duration.min),
          maxDuration: formatDuration(bestItinerary.duration.max),
          meanDuration: formatDuration(Math.round(bestItinerary.duration.mean)),
        },
      ),
    };

    const departureFaq = {
      id: `departureFaq-${mean}`,
      question: this.intl.formatMessage(
        messages[
          SHORT_FAQ_DEPQRTURE_HEADERS[seed % SHORT_FAQ_DEPQRTURE_HEADERS.length]
        ],
        {
          origin: state.originCity.name,
          destination: state.destinationCity.name,
          transportMean: translatedMean,
        },
      ),
      answer: this.intl.formatMessage(
        messages[SHORT_FAQ_DEPARTURE[seed % SHORT_FAQ_DEPARTURE.length]],
        {
          transportMean: translatedMean,
          formattedTrip1: formattedTrips[0],
          formattedTrip2: formattedTrips[1],
          firstDeparture: formatDuration(bestItinerary.departureTsOfFirst),
          lastDeparture: formatDuration(bestItinerary.departureTsOfLast),
        },
      ),
    };

    const dailyTripsFaq = {
      id: `dailyTripsFaq-${mean}`,
      question: this.intl.formatMessage(
        messages[
          SHORT_FAQ_DAILY_TRIPS_HEADERS[
            seed % SHORT_FAQ_DAILY_TRIPS_HEADERS.length
          ]
        ],
        {
          origin: state.originCity.name,
          destination: state.destinationCity.name,
          transportMean: translatedMean,
        },
      ),
      answer: this.intl.formatMessage(
        messages[SHORT_FAQ_DAILY_TRIPS[seed % SHORT_FAQ_DAILY_TRIPS.length]],
        {
          transportMean: translatedMean,
          formattedTrip1: formattedTrips[0],
          formattedTrip2: formattedTrips[1],
          dailyTrips: nbPerDay,
        },
      ),
    };

    return [priceFaq, durationFaq, departureFaq, dailyTripsFaq];
  }
}

export { FaqTextGen };
